import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  USER_VALUE_CURRENT
} from '@databases/types/database';
import { createElement, type ReactNode } from 'react';

import {
  defineFieldReadModule,
  type FieldReadModule,
  type FieldReadResource,
  type FieldReadResourceState
} from '../field-read-module.ts';
import {
  EMPTY_USER_CONTENT,
  type UserReadContent,
  type UserItemContent
} from './user-read-content.ts';
import { UserValueView } from './user-value-view.tsx';

export type UserResourceValue = {
  readonly accountId: string;
  readonly displayName: string;
  readonly avatarUrl: string | undefined;
};

export type UserReadPolicy = {
  readonly getUserSnapshot?: (accountId: string) => UserResourceValue | undefined;
  readonly getFallbackName?: (
    accountId: string,
    storedName: string | undefined
  ) => string | undefined;
  readonly currentUserId?: string;
  readonly getUserResource?: (accountId: string) => FieldReadResource<UserResourceValue>;
};

type CompiledUser = {
  readonly key: string;
  readonly accountId: string;
  readonly storedDisplayName: string | undefined;
};

function compileUsers(
  value: DatabaseValue | undefined,
  currentUserId: string | undefined
): readonly CompiledUser[] {
  if (value?.type !== DatabaseFieldType.USER) {
    return [];
  }

  if (!Array.isArray(value.value)) {
    return [];
  }

  return value.value.flatMap((item) => {
    if (!('accountId' in item) || typeof item.accountId !== 'string') {
      return [];
    }

    const accountId =
      item.accountId === USER_VALUE_CURRENT && currentUserId
        ? currentUserId
        : item.accountId;

    return [
      Object.freeze({
        key: item._id,
        accountId,
        storedDisplayName:
          !(item.accountId === USER_VALUE_CURRENT && currentUserId) &&
          'displayName' in item &&
          typeof item.displayName === 'string'
            ? item.displayName
            : undefined
      })
    ];
  });
}

function createContent(
  users: readonly CompiledUser[],
  stateByAccountId: ReadonlyMap<string, FieldReadResourceState<UserResourceValue>>
): UserReadContent {
  const contentUsers = users.map((user): UserItemContent => {
    const state = stateByAccountId.get(user.accountId);
    const resolved = state?.status === 'ready' ? state.value : undefined;

    return Object.freeze({
      key: user.key,
      accountId: user.accountId,
      displayName: resolved?.displayName ?? (user.storedDisplayName || undefined),
      avatarName: resolved?.displayName ?? '',
      avatarUrl: resolved?.avatarUrl,
      status: state?.status ?? 'ready'
    });
  });

  return contentUsers.length === 0
    ? EMPTY_USER_CONTENT
    : Object.freeze({ kind: 'user', users: Object.freeze(contentUsers) });
}

export type UserReadRenderer = (content: UserReadContent) => ReactNode;
const renderUser: UserReadRenderer = (content) =>
  createElement(UserValueView, { content });

/**
 * Compiles User defaults, current-user identity, and bounded account reads.
 */
export function createUserFieldReadModule(
  options?: UserReadPolicy & { readonly render?: UserReadRenderer }
): FieldReadModule<UserReadContent> {
  const { render = renderUser, ...policy } = options ?? {};

  return defineFieldReadModule<UserReadContent>({
    fieldType: DatabaseFieldType.USER,
    render,
    compile(field: DatabaseField) {
      function getUsers(
        storedValue: DatabaseValue | undefined,
        defaultValue: DatabaseValue | undefined
      ) {
        const value = storedValue ?? defaultValue;

        return compileUsers(value, policy.currentUserId).map((user) =>
          Object.freeze({
            ...user,
            storedDisplayName: policy.getFallbackName
              ? policy.getFallbackName(user.accountId, user.storedDisplayName)
              : user.storedDisplayName
          })
        );
      }

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ) {
          const users = getUsers(storedValue, defaultValue);

          return createContent(
            users,
            new Map(
              users.flatMap((user) => {
                const value = policy.getUserSnapshot?.(user.accountId);

                return value
                  ? [[user.accountId, { status: 'ready', value } as const] as const]
                  : [];
              })
            )
          );
        },
        getResourceRead({ storedValue, defaultValue }) {
          if (!policy.getUserResource) {
            return undefined;
          }

          const users = getUsers(storedValue, defaultValue);

          if (users.length === 0) {
            return undefined;
          }

          const resourcesByAccountId = new Map<
            string,
            FieldReadResource<UserResourceValue>
          >();
          const cached = new Map<string, FieldReadResourceState<UserResourceValue>>();

          for (const user of users) {
            const available = policy.getUserSnapshot?.(user.accountId);

            if (available) {
              cached.set(user.accountId, { status: 'ready', value: available });
              continue;
            }

            if (!resourcesByAccountId.has(user.accountId)) {
              resourcesByAccountId.set(
                user.accountId,
                policy.getUserResource(user.accountId)
              );
            }
          }

          if (resourcesByAccountId.size === 0) {
            return undefined;
          }

          const loadingState = Object.freeze<FieldReadResourceState<UserResourceValue>>({
            status: 'loading'
          });
          const loadingContent = createContent(
            users,
            new Map([
              ...users.map((user) => [user.accountId, loadingState] as const),
              ...cached
            ])
          );

          return Object.freeze({
            resources: Object.freeze([...resourcesByAccountId.values()]),
            resolve(
              read: <Value>(
                resource: FieldReadResource<Value>
              ) => FieldReadResourceState<Value>
            ) {
              let allLoading = true;
              const stateByAccountId = new Map<
                string,
                FieldReadResourceState<UserResourceValue>
              >(cached);

              for (const [accountId, resource] of resourcesByAccountId) {
                const state = read(resource) as FieldReadResourceState<UserResourceValue>;

                allLoading = allLoading && state.status === 'loading';
                stateByAccountId.set(accountId, state);
              }

              return allLoading ? loadingContent : createContent(users, stateByAccountId);
            }
          });
        }
      });
    }
  });
}
